import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../../components/modal-escape-guard/modal-escape-guard.directive';
import { TestPlanService } from '../../../services/test-plan.service';
import { ReleaseService } from '../../../services/release.service';
import { TestCaseService } from '../../../services/test-case.service';
import { ProjectMemberService } from '../../../services/project-member.service';
import { AccountService } from '../../../account/account.service';
import { prefillOwnerWithCurrentUser } from '../../../shared/forms/owner-prefill';
import {
  TEST_PLAN_STATUSES,
  TEST_PLAN_TYPES,
  TestPlan,
  TestPlanExecution,
  TestPlanExecutionKind,
  TestPlanRun,
  TestPlanStatus,
  TestPlanType,
} from '../../../models/test-plan.model';
import { TestRunService } from '../../../services/test-run.service';
import { ProjectRoleService } from '../../../services/project-role.service';
import {
  buildTestPlanPickerSavedViewFiltersJson,
  caseExecutionKind,
  matchesExecutionFilter,
  parseTestPlanPickerSavedViewFilters,
  PickerExecutionFilter,
  realRunQueryParams,
  writeErrorKey,
  normalizeRuleTags,
  sameRule,
  matchesRule,
  testPlanType,
} from '../test-plans.utils';
import { SavedViewService } from '../../../services/saved-view.service';
import { SavedView } from '../../../models/saved-view.model';
import { SavedViewOption, SavedViewsMenuComponent } from '../../../shared/saved-views/saved-views-menu.component';
import { TestRun } from '../../../models/test-run.model';
import { Release } from '../../../models/release.model';
import { TEST_TYPES, TestCase, TestCasePriority, TestStep } from '../../../models/test-case.model';
import { TcValueLabelPipe, standardValuesFirst, tcValueLabel } from '../../../pipes/tc-value-label.pipe';
import { ProjectMemberDTO } from '../../../models/project-member.model';
import { PersonOption, PersonSelectComponent } from '../../../components/person-select/person-select.component';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { testRunExecutionsLink } from '../../../utils/test-run-routes';
import { warnBeforeUnload } from '../../../shared/forms/warn-before-unload';
import { SaveToastService } from '../../../components/save-toast/save-toast.service';
import { TestpaketeKarteComponent } from '../../../components/testpakete-karte/testpakete-karte.component';
import { PaketEntwurf, TestpaketeStore } from '../../../components/testpakete-karte/testpakete.store';
import { PaketTestfallDialogComponent } from '../../../components/paket-testfall-dialog/paket-testfall-dialog.component';
import { PaketBelegung, PaketTestfall } from '../../../components/paket-testfall-dialog/paket-testfall-dialog.types';
import { isProjectArchived, isStaleVersion, serverMessage } from '../../../shared/http/server-message';
import { SegmentedFilterControlComponent } from '../../../components/segmented-filter-control/segmented-filter-control.component';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { SelectionChangeDialogComponent } from './selection-change-dialog/selection-change-dialog.component';
import { TagFilterComponent, TagFilterOption } from '../../../components/tag-filter/tag-filter.component';
import { countTagOptions, tagMatch } from '../../../components/tag-filter/tag-filter.utils';
import {
  TestPlanAnleger,
  TestPlanDetailsHistorieComponent,
} from '../test-plan-details-historie/test-plan-details-historie.component';

interface TestPlanFormShape {
  title: FormControl<string>;
  releaseId: FormControl<string>;
  assigneeUserId: FormControl<string>;
  status: FormControl<TestPlanStatus>;
  type: FormControl<TestPlanType>;
  tags: FormControl<string[]>;
  notes: FormControl<string>;
}

export type ExecutionKind = 'MANUAL' | 'AUTOMATED' | 'MIXED' | 'NONE';

/** Entwurf der Testpakete, den das Formular nach einem gescheiterten Paket-Speichern beim Anlegen weitergibt (P27). */
const PENDING_PACKAGES = 'pendingPackages';

export type EchtlaufBlock = 'closed' | 'mixed' | 'dirty';

const EXECUTION_FILTER_LABELS: Record<PickerExecutionFilter, string> = {
  MANUAL: 'testPlans.form.picker.executionManual',
  AUTOMATABLE: 'testPlans.form.picker.executionAutomatable',
  AUTOMATED: 'testPlans.form.picker.executionAutomated',
};

// Backend-Grenzen der Testplan-Tags (TestPlanTags), geprüft nach der Normalisierung.
const MAX_TAGS = 20;
const MAX_TAG_LENGTH = 50;
const TAGS_ERROR = 'tagsInvalid';

function tagsValidator(control: AbstractControl<string[]>): ValidationErrors | null {
  const tags = control.value ?? [];
  const invalid = tags.length > MAX_TAGS || tags.some((t) => t.length > MAX_TAG_LENGTH);
  return invalid ? { [TAGS_ERROR]: true } : null;
}

function isTagsInvalidResponse(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 400 && error.error?.code === 'TAGS_INVALID';
}

@Component({
  selector: 'app-test-plan-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    TcValueLabelPipe,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatSelect,
    MatOption,
    RouterLink,
    PersonSelectComponent,
    Modal,
    ModalEscapeGuardDirective,
    SavedViewsMenuComponent,
    TestpaketeKarteComponent,
    PaketTestfallDialogComponent,
    SegmentedFilterControlComponent,
    ConfirmDialogComponent,
    SelectionChangeDialogComponent,
    TagFilterComponent,
    TestPlanDetailsHistorieComponent,
  ],
  providers: [TestpaketeStore],
  templateUrl: './test-plan-form.component.html',
  styleUrl: './test-plan-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestPlanFormComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(NonNullableFormBuilder);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private planService = inject(TestPlanService);
  private releaseService = inject(ReleaseService);
  private testCaseService = inject(TestCaseService);
  private projectMemberService = inject(ProjectMemberService);
  private snackBar = inject(MatSnackBar);
  private accountService = inject(AccountService);
  private translate = inject(TranslateService);
  private testRunService = inject(TestRunService);
  private projectRoleService = inject(ProjectRoleService);
  private savedViewService = inject(SavedViewService);
  private saveToast = inject(SaveToastService);
  private location = inject(Location);
  protected readonly paketStore = inject(TestpaketeStore);

  readonly projectId: string;
  planId: string | null;
  readonly isEdit: boolean;

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly vorlageNotFound = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal(false);
  readonly saveErrorKey = signal('testPlans.form.saveError');
  readonly isReadOnly = computed(
    () =>
      this.projectRoleService.isArchived() ||
      (this.projectRoleService.isProjectReader() && !this.projectRoleService.isTenantAdmin()),
  );
  readonly titleKey = computed(() => {
    if (!this.isEdit) return 'testPlans.form.createTitle';
    return this.isReadOnly() ? 'testPlans.form.viewTitle' : 'testPlans.form.editTitle';
  });
  readonly umgebungAnlegbar = computed(() => !this.isReadOnly() && this.projectRoleService.canManageProject());
  readonly plan = signal<TestPlan | null>(null);
  readonly testCases = signal<TestCase[]>([]);
  readonly testCasesLoadError = signal(false);
  readonly users = signal<ProjectMemberDTO[]>([]);
  readonly releases = signal<Release[]>([]);
  // Bei einem Ladefehler ist die Liste ebenfalls leer; der Hinweis gilt nur für eine erfolgreich geladene Liste.
  private readonly releasesLoaded = signal(false);
  readonly releasesEmpty = computed(() => this.releasesLoaded() && this.releases().length === 0);
  readonly selectedIds = signal<Set<string>>(new Set());
  readonly initialSelectedIds = signal<Set<string>>(new Set());

  readonly stepSelections = signal<Map<string, Set<number>>>(new Map());
  readonly expandedStepsCaseId = signal<string | null>(null);
  readonly stepsByCase = signal<Map<string, TestStep[]>>(new Map());
  readonly loadingSteps = signal<Set<string>>(new Set());

  readonly addMode = signal(true);

  readonly search = signal('');
  readonly testTypeFilter = signal<string>('');
  readonly priorityFilter = signal<TestCasePriority | ''>('');
  readonly executionFilter = signal<PickerExecutionFilter | ''>('');
  readonly onlyMatchingKind = signal(true);
  readonly tagFilter = signal<string[]>([]);
  readonly ruleTags = signal<string[]>([]);

  /** Die Wahl im Segment; `executionKind` dagegen beschreibt, was gerade ausgewählt ist. */
  readonly planKind = signal<TestPlanExecutionKind | null>(null);
  readonly kindHint = signal<TestCase | null>(null);
  readonly pendingKindChange = signal<TestPlanExecutionKind | null>(null);
  protected readonly caseExecutionKind = caseExecutionKind;

  readonly savedViews = signal<SavedView[]>([]);
  readonly activeSavedViewName = signal<string | null>(null);
  readonly savedViewsError = signal<string | null>(null);
  readonly savedViewsOptions = computed<SavedViewOption[]>(() =>
    this.savedViews().map((v) => ({ name: v.name, summary: this.summarizeSavedView(v) })),
  );

  readonly fieldLengths = FIELD_LENGTHS;

  readonly execution = signal<TestPlanExecution | null>(null);
  private pollingSub: Subscription | null = null;

  readonly isRunning = computed(() => {
    const status = this.execution()?.status;
    return status === 'PENDING' || status === 'RUNNING';
  });
  readonly isDone = computed(() => this.execution()?.status === 'DONE');
  readonly isFailed = computed(() => this.execution()?.status === 'FAILED');

  /** DONE heißt nur „Testlauf angelegt“; Resultate gibt es erst, wenn der Lauf selbst abgeschlossen ist. */
  readonly createdRun = signal<TestRun | null>(null);
  private createdRunSub: Subscription | null = null;
  readonly createdRunId = computed(() => (this.isDone() ? (this.execution()?.testRunId ?? null) : null));
  readonly runCompleted = computed(() => !!this.createdRunId() && this.createdRun()?.status === 'COMPLETED');
  readonly showRunCreated = computed(() => !!this.createdRunId() && !this.runCompleted());
  readonly createdRunLink = computed(() => testRunExecutionsLink(this.projectId, this.createdRunId() ?? ''));

  readonly form: FormGroup<TestPlanFormShape> = this.fb.group({
    title: this.fb.control<string>('', [Validators.required, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]),
    releaseId: this.fb.control<string>('', [Validators.required]),
    assigneeUserId: this.fb.control<string>('', [Validators.required]),
    status: this.fb.control<TestPlanStatus>('ENTWURF'),
    type: this.fb.control<TestPlanType>('FREIGABE'),
    tags: this.fb.control<string[]>([], [tagsValidator]),
    notes: this.fb.control<string>('', [Validators.maxLength(FIELD_LENGTHS.TEST_PLAN_NOTES_MAX)]),
  });

  readonly statusOptions = TEST_PLAN_STATUSES;
  readonly typeOptions = TEST_PLAN_TYPES;

  readonly hasTestCases = computed(() => this.selectedIds().size > 0);

  /** Über die Gruppe statt das Control, weil der Edit-Modus ohne Control-Events vorbelegt. */
  private readonly releaseIdValue = toSignal(
    this.form.valueChanges.pipe(map(() => this.form.controls.releaseId.value)),
    {
      initialValue: this.form.controls.releaseId.value,
    },
  );

  /**
   * Die Release-Liste kann (z. B. bei einem Ladefehler) leer sein; im Edit-Modus
   * trägt der Plan selbst noch das Label seines Releases.
   */
  readonly selectedReleaseLabel = computed<string | null>(() => {
    const id = this.releaseIdValue();
    if (!id) return null;
    const known = this.releases().find((r) => r.id === id)?.label;
    if (known) return known;
    const plan = this.plan();
    return plan?.releaseId === id ? plan.releaseLabel : id;
  });

  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.users()));

  readonly availableTestTypes = computed(() =>
    standardValuesFirst(
      TEST_TYPES,
      this.testCases().flatMap((tc) => tc.testType ?? []),
    ),
  );

  readonly availablePriorities = computed(() => {
    const set = new Set<TestCasePriority>();
    for (const tc of this.testCases()) {
      if (tc.priority) set.add(tc.priority);
    }
    return Array.from(set).sort();
  });

  readonly filteredCandidates = computed(() => {
    const term = this.search().toLowerCase().trim();
    const testTypeF = this.testTypeFilter();
    const prioF = this.priorityFilter();
    const executionF = this.executionFilter();
    const kind = this.planKind();
    const tagsF = this.tagFilter();
    return this.testCases().filter((tc) => {
      if (term) {
        const hay =
          `${tc.title} ${tc.key ?? ''} ${tc.requirementLabel ?? ''} ${(tc.tags ?? []).join(' ')}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (testTypeF && tc.testType !== testTypeF) return false;
      if (prioF && tc.priority !== prioF) return false;
      if (!tagMatch(tc.tags, tagsF)) return false;
      return matchesExecutionFilter(tc, executionF, kind);
    });
  });

  readonly tagOptions = computed<TagFilterOption[]>(() => countTagOptions(this.testCases(), this.tagFilter()));

  readonly selectedTestCases = computed(() => {
    const selected = this.selectedIds();
    return this.testCases().filter((tc) => selected.has(tc.id));
  });

  readonly selectedInPlanOrder = computed(() => {
    const byId = new Map(this.testCases().map((tc) => [tc.id, tc]));
    return [...this.selectedIds()].flatMap((id) => byId.get(id) ?? []);
  });

  /** `null`, solange das Release nicht in der geladenen Liste steht (etwa nach einem Ladefehler). */
  readonly selectedRelease = computed(() => this.releases().find((r) => r.id === this.releaseIdValue()) ?? null);

  readonly paketTestfaelle = computed<PaketTestfall[]>(() =>
    this.selectedTestCases().map((tc) => ({ id: tc.id, key: tc.key ?? tc.id, title: tc.title, tags: tc.tags ?? [] })),
  );

  readonly paketBelegung = computed<PaketBelegung[]>(() =>
    this.paketStore.entwurf().map((p) => ({ key: p.key, name: p.name, testCaseIds: p.testCaseIds })),
  );

  readonly hiddenByKindCount = computed(() => this.filteredCandidates().filter((tc) => this.isHiddenByKind(tc)).length);

  readonly visibleCandidates = computed(() =>
    this.addMode() ? this.filteredCandidates().filter((tc) => !this.isHiddenByKind(tc)) : this.selectedInPlanOrder(),
  );

  private readonly casesOfSelectAll = computed(() => {
    const rows = this.visibleCandidates();
    const kind = this.planKind() ?? (rows.length ? caseExecutionKind(rows[0]) : null);
    return rows.filter((tc) => caseExecutionKind(tc) === kind);
  });

  readonly allOfKindSelected = computed(() => {
    const cases = this.casesOfSelectAll();
    const selected = this.selectedIds();
    return cases.length > 0 && cases.every((tc) => selected.has(tc.id));
  });

  readonly selectedCount = computed(() => this.selectedIds().size);
  readonly candidatesCount = computed(() => this.testCases().length);

  readonly steps = [1, 2] as const;
  /** Leert sich die Auswahl, beginnt die nächste wieder bei Schritt 1. */
  private readonly chosenStep = linkedSignal<boolean, 1 | 2>({
    source: () => this.selectedCount() === 0,
    computation: (empty, previous) => (empty ? 1 : (previous?.value ?? 1)),
  });
  readonly activeStep = computed<1 | 2>(() => {
    const chosen = this.chosenStep();
    return this.selectedCount() === 0 ? 1 : chosen;
  });

  readonly selectionDirty = computed(() => {
    const current = this.selectedIds();
    const initial = this.initialSelectedIds();
    if (current.size !== initial.size) return true;
    for (const id of current) {
      if (!initial.has(id)) return true;
    }
    return false;
  });

  readonly executionKind = computed<ExecutionKind>(() => {
    const rows = this.selectedTestCases();
    if (rows.length === 0) return 'NONE';
    let manual = false;
    let automated = false;
    for (const tc of rows) {
      if (caseExecutionKind(tc) === 'AUTOMATED') automated = true;
      else manual = true;
      if (manual && automated) return 'MIXED';
    }
    return automated ? 'AUTOMATED' : 'MANUAL';
  });

  readonly executionCounts = computed(() => {
    const rows = this.selectedTestCases();
    const automated = rows.filter((tc) => caseExecutionKind(tc) === 'AUTOMATED').length;
    return { manual: rows.length - automated, automated };
  });

  readonly isMixed = computed(() => this.executionKind() === 'MIXED');

  readonly pendingKindChangeCount = computed(() => {
    const kind = this.pendingKindChange();
    return this.selectedTestCases().filter((tc) => caseExecutionKind(tc) !== kind).length;
  });

  readonly dirty = signal(false);
  private readonly formStatus = toSignal(this.form.statusChanges.pipe(startWith(this.form.status)), {
    initialValue: this.form.status,
  });
  private readonly statusValue = toSignal(this.form.valueChanges.pipe(map(() => this.form.controls.status.value)), {
    initialValue: this.form.controls.status.value,
  });
  readonly tagsValue = toSignal(this.form.valueChanges.pipe(map(() => this.form.controls.tags.value)), {
    initialValue: this.form.controls.tags.value,
  });
  readonly notesValue = toSignal(this.form.valueChanges.pipe(map(() => this.form.controls.notes.value)), {
    initialValue: this.form.controls.notes.value,
  });
  readonly anleger = signal<TestPlanAnleger | null>(null);
  readonly historieReadonly = computed(() => this.isReadOnly() || !!this.plan()?.archivedAt);
  private readonly historie = viewChild(TestPlanDetailsHistorieComponent);
  readonly tagsInvalid = computed(() => {
    this.formStatus();
    this.tagsValue();
    return this.form.controls.tags.hasError(TAGS_ERROR);
  });

  readonly echtlaufBlock = computed<EchtlaufBlock | null>(() => {
    if (this.executionKind() === 'MIXED') return 'mixed';
    if (this.dirty() || this.selectionDirty()) return 'dirty';
    if (this.statusValue() === 'ABGESCHLOSSEN') return 'closed';
    return null;
  });
  readonly saveDisabled = computed(() => {
    if (this.saving() || this.paketStore.umgebungAnlage() || this.formStatus() !== 'VALID') return true;
    if (this.testCasesLoadError()) return true;
    if (!this.hasTestCases() || !this.planKind() || this.isMixed()) return true;
    if (this.isEdit && !this.dirty() && !this.selectionDirty() && !this.paketStore.geaendert()) return true;
    return false;
  });

  /** Nach dem Anlegen wechselt das Formular auch mit ungespeicherten Paketen ohne Rückfrage (P27). */
  private leaveWithoutPrompt = false;
  private pendingPackages: PaketEntwurf[] | null = null;

  /** Sichtbarkeit der App-Modal für die Verlassen-Bestätigung (NEX-814, ersetzt den nativen Browser-Dialog). */
  readonly showDeactivateConfirm = signal(false);

  readonly selectionDialog = signal<{ added: string[]; removed: string[]; reopensPlan: boolean } | null>(null);
  readonly selectionRuns = signal<TestPlanRun[] | null>(null);
  readonly selectionRunsLoading = signal(false);
  /** Überlebt ein gescheitertes Speichern, damit der nächste Versuch sie nicht verliert. */
  readonly changeReason = signal('');
  private readonly deactivateConfirm$ = new Subject<boolean>();

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    this.planId = this.route.snapshot.paramMap.get('planId');
    this.isEdit = !!this.planId;
    this.addMode.set(!this.isEdit);

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.form.dirty) this.dirty.set(true);
    });

    if (this.projectRoleService.isArchived() || (this.isReadOnly() && !this.isEdit)) {
      this.snackBar.open(this.translate.instant('testPlans.form.noWriteRight'), undefined, { duration: 4000 });
      this.router.navigate(['/projects', this.projectId, 'test-plans']);
      return;
    }
    if (this.isReadOnly()) this.form.disable();
    this.loadPackages();
    this.loadInitialData();
    if (!this.isReadOnly()) this.loadSavedViews();
  }

  private loadPackages(): void {
    // Der Router hat den History-State der laufenden Navigation vor dem Erzeugen der Komponente geschrieben.
    const pending: unknown = (this.location.getState() as Record<string, unknown> | null)?.[PENDING_PACKAGES];
    this.pendingPackages = Array.isArray(pending) ? (pending as PaketEntwurf[]) : null;
    this.paketStore.init(this.projectId, this.planId);
    this.paketStore.laden();
    this.paketStore.ladenNachschlag();
    effect(() => {
      const release = this.selectedRelease();
      if (this.paketStore.ladeStatus() !== 'bereit' || this.isReadOnly()) return;
      untracked(() => {
        this.applyPendingPackages();
        if (release) this.paketStore.entferneFremdeStufen(release);
      });
    });
  }

  /** Einmalig; danach aus dem History-State entfernt, damit ein Neuladen den Entwurf nicht erneut einspielt. */
  private applyPendingPackages(): void {
    const pending = this.pendingPackages;
    if (!pending) return;
    this.pendingPackages = null;
    this.paketStore.setzeEntwurf(pending);
    this.chosenStep.set(2);
    const state = { ...((this.location.getState() ?? {}) as Record<string, unknown>) };
    delete state[PENDING_PACKAGES];
    this.location.replaceState(this.location.path(), '', state);
  }

  private loadInitialData(): void {
    // NEX-829: Projekt-Mitglieder statt des TENANT_ADMIN-only Customers/Users-Endpunkts —
    // ein Nutzer mit Rolle USER darf diesen Endpunkt aufrufen und bekommt so
    // Verantwortlichen-Kandidaten (analog NEX-822).
    const users$ = this.projectMemberService.getMembers(this.projectId).pipe(
      catchError(() => {
        this.snackBar.open(this.translate.instant('testPlans.form.usersLoadError'), undefined, { duration: 4000 });
        return of([] as ProjectMemberDTO[]);
      }),
    );
    const releases$ = this.releaseService.list(this.projectId).pipe(
      tap(() => this.releasesLoaded.set(true)),
      catchError(() => {
        this.snackBar.open(this.translate.instant('testPlans.form.releasesLoadError'), undefined, { duration: 4000 });
        return of([] as Release[]);
      }),
    );
    const testCases$ = this.testCaseService.list(this.projectId).pipe(
      catchError(() => {
        this.testCasesLoadError.set(true);
        return of([] as TestCase[]);
      }),
    );
    const plan$ =
      this.isEdit && this.planId
        ? this.planService.getById(this.projectId, this.planId).pipe(
            map((p) => p as TestPlan | null),
            catchError(() => of(null)),
          )
        : of(null);
    const vorlageId = this.isEdit ? null : this.route.snapshot.queryParamMap.get('vorlage');
    // get statt instant: Beim Direktaufruf der Adresse können die Texte noch fehlen, sonst würde der Schlüssel als Titel gespeichert.
    const vorlage$ = vorlageId
      ? this.planService.getById(this.projectId, vorlageId).pipe(
          switchMap((vorlage) =>
            this.translate
              .get('testPlans.form.vorlageTitle', { title: vorlage.title })
              .pipe(map((title: string) => ({ ...vorlage, title }) as TestPlan | null)),
          ),
          catchError(() => {
            this.vorlageNotFound.set(true);
            return of(null);
          }),
        )
      : of(null);
    // NEX-824: nur im Anlegen-Modus gebraucht (Self-Default); im Edit-Modus bleibt
    // der geladene Verantwortliche unverändert, daher kein zusätzlicher Request.
    const profile$ = this.isEdit ? of(null) : this.accountService.getProfile().pipe(catchError(() => of(null)));

    forkJoin({
      users: users$,
      releases: releases$,
      testCases: testCases$,
      plan: plan$,
      vorlage: vorlage$,
      profile: profile$,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ users, releases, testCases, plan, vorlage, profile }) => {
          this.users.set(users);
          this.releases.set(releases);
          this.testCases.set(testCases);
          if (plan) {
            this.plan.set(plan);
            this.form.patchValue(
              {
                title: plan.title,
                releaseId: plan.releaseId ?? '',
                assigneeUserId: plan.assigneeUserId ?? '',
                status: plan.status,
                type: testPlanType(plan),
                tags: plan.tags ?? [],
                notes: plan.notes ?? '',
              },
              { emitEvent: false },
            );
            this.form.updateValueAndValidity();
            const set = new Set(plan.testCaseIds);
            this.selectedIds.set(set);
            this.initialSelectedIds.set(new Set(set));
            this.planKind.set(this.initialKind(plan));
            this.ruleTags.set(plan.selectionRuleTags ?? []);
            this.hydrateStepSelections(plan);
            this.loadExistingExecution(plan.id);
          } else if (this.isEdit) {
            this.loadError.set(true);
          } else {
            this.prefillRelease(releases);
            if (profile) {
              this.anleger.set({ id: profile.id, displayName: profile.displayName });
              // NEX-824: Self-Default — nur vorbelegen, wenn der aktuelle Nutzer unter
              // den geladenen Kandidaten ist; Pflichtfeld-Validierung ist hier bewusst
              // nicht Teil dieses Tickets (siehe NEX-819).
              prefillOwnerWithCurrentUser(this.form.controls.assigneeUserId, profile.id, this.personOptions());
            }
            if (vorlage) this.applyVorlage(vorlage);
          }
          this.form.markAsPristine();
          if (vorlage) this.dirty.set(true);
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  /** Die Testfälle gehen vor der gespeicherten Art: Sonst führte ein inzwischen automatisierter Testfall in einen 400 ohne Ausweg. */
  private initialKind(plan: TestPlan): TestPlanExecutionKind | null {
    const fromCases = this.executionKind();
    if (fromCases === 'MIXED') return null;
    if (fromCases !== 'NONE') return fromCases;
    return plan.executionMode === 'MIXED' ? null : (plan.executionMode ?? null);
  }

  /** Der Plan bleibt ungesetzt, sonst griffe die Bearbeiten-Logik; Release, Fälligkeit, Status und Notizen gehören zur Vorlage selbst. */
  private applyVorlage(vorlage: TestPlan): void {
    const assignee = vorlage.assigneeUserId;
    this.form.patchValue({
      title: vorlage.title,
      type: testPlanType(vorlage),
      tags: vorlage.tags ?? [],
      ...(this.personOptions().some((option) => option.id === assignee) ? { assigneeUserId: assignee! } : {}),
    });
    this.selectedIds.set(new Set(vorlage.testCaseIds));
    this.planKind.set(this.initialKind(vorlage));
    this.hydrateStepSelections(vorlage);
    // Der Knopf bleibt ohne Release gesperrt; erst `touched` zeigt, warum.
    this.form.controls.releaseId.markAsTouched();
  }

  private prefillRelease(releases: Release[]): void {
    const requested = this.route.snapshot.queryParamMap.get('release');
    if (releases.some((r) => r.id === requested)) this.form.controls.releaseId.setValue(requested!);
  }

  private hydrateStepSelections(plan: TestPlan): void {
    const map = new Map<string, Set<number>>();
    const raw = plan.stepSelections ?? {};
    for (const [caseId, idx] of Object.entries(raw)) {
      map.set(caseId, new Set(idx));
    }
    this.stepSelections.set(map);
  }

  private loadExistingExecution(planId: string): void {
    this.planService
      .getExecution(this.projectId, planId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((exec) => {
        if (exec) {
          this.applyExecution(exec);
          if (exec.status === 'PENDING' || exec.status === 'RUNNING') {
            this.startPolling(planId);
          }
        }
      });
  }

  toggle(tc: TestCase): void {
    if (this.saving() || this.isReadOnly()) return;
    if (this.isKindLocked(tc)) {
      this.kindHint.set(tc);
      return;
    }
    this.selectedIds.update((set) => {
      const next = new Set(set);
      if (next.has(tc.id)) {
        next.delete(tc.id);
      } else {
        next.add(tc.id);
      }
      return next;
    });
    this.kindHint.set(null);
    this.onSelectionChanged();
  }

  /** Eine abgelehnte Wahl (gesperrte Art) hat das Häkchen im DOM schon umgestellt. */
  onRowCheckboxChange(event: Event, tc: TestCase): void {
    this.toggle(tc);
    (event.target as HTMLInputElement).checked = this.isSelected(tc);
  }

  /** Nur Tastatur auf der Zeile selbst: Checkbox und Buttons darin bedienen sich selbst. */
  onRowKey(event: Event, tc: TestCase): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.toggle(tc);
  }

  isKindLocked(tc: TestCase): boolean {
    const kind = this.planKind();
    return !!kind && !this.isSelected(tc) && caseExecutionKind(tc) !== kind;
  }

  private isHiddenByKind(tc: TestCase): boolean {
    return this.onlyMatchingKind() && this.isKindLocked(tc);
  }

  toggleAllOfKind(): void {
    if (this.saving()) return;
    const select = !this.allOfKindSelected();
    const cases = this.casesOfSelectAll();
    this.selectedIds.update((set) => {
      const next = new Set(set);
      for (const tc of cases) {
        if (select) next.add(tc.id);
        else next.delete(tc.id);
      }
      return next;
    });
    this.onSelectionChanged();
  }

  onSelectAllChange(event: Event): void {
    this.toggleAllOfKind();
    (event.target as HTMLInputElement).checked = this.allOfKindSelected();
  }

  /** Ohne Art legt die erste eindeutige Auswahl sie fest; das bereinigt auch einen gemischten Plan per Abwählen. */
  private onSelectionChanged(): void {
    this.dirty.set(true);
    const selected = this.executionKind();
    if (!this.planKind() && (selected === 'MANUAL' || selected === 'AUTOMATED')) this.planKind.set(selected);
  }

  onTagFilterChange(tags: string[]): void {
    this.tagFilter.set(tags);
    this.activeSavedViewName.set(null);
  }

  /** Mit Art nur deren Testfälle; ohne Art alle, dann meldet der Gemischt-Alarm den Rest. */
  adoptRule(): void {
    if (this.saving()) return;
    const rule = normalizeRuleTags(this.tagFilter());
    const kind = this.planKind();
    const matching = this.testCases().filter(
      (tc) => matchesRule(tc, rule) && (!kind || caseExecutionKind(tc) === kind),
    );
    this.ruleTags.set(rule);
    this.selectedIds.update((set) => new Set([...set, ...matching.map((tc) => tc.id)]));
    this.kindHint.set(null);
    this.onSelectionChanged();
    this.focusAfterRuleChange('tp-remove-rule');
  }

  /** Die aufgenommenen Testfälle bleiben im Plan. */
  removeRule(): void {
    if (this.saving()) return;
    this.ruleTags.set([]);
    this.dirty.set(true);
    this.focusAfterRuleChange('tp-adopt-rule');
  }

  /** Der geklickte Button verschwindet; ohne Gegenstück (Liste der gewählten) übernimmt der aktive Reiter. */
  private focusAfterRuleChange(testId: string): void {
    afterNextRender(
      () => {
        const host = this.hostElement.nativeElement;
        const target =
          host.querySelector<HTMLElement>(`[data-testid="${testId}"]`) ??
          host.querySelector<HTMLElement>(`#tp-step-tab-${this.activeStep()}`);
        target?.focus();
      },
      { injector: this.injector },
    );
  }

  onKindChange(value: string | null): void {
    const kind = value as TestPlanExecutionKind;
    this.kindHint.set(null);
    if (this.isMixed()) {
      this.keepOnlyKind(kind);
    } else if (this.selectedTestCases().some((tc) => caseExecutionKind(tc) !== kind)) {
      this.pendingKindChange.set(kind);
    } else {
      this.planKind.set(kind);
      this.dirty.set(true);
    }
  }

  /** Nicht geladene Testfälle bleiben stehen; über sie entscheidet das Backend beim Speichern. */
  keepOnlyKind(kind: TestPlanExecutionKind): void {
    const otherKind = new Set(
      this.selectedTestCases()
        .filter((tc) => caseExecutionKind(tc) !== kind)
        .map((tc) => tc.id),
    );
    this.selectedIds.update((set) => new Set([...set].filter((id) => !otherKind.has(id))));
    this.planKind.set(kind);
    this.dirty.set(true);
  }

  onKindChangeConfirmed(): void {
    const kind = this.pendingKindChange();
    this.pendingKindChange.set(null);
    if (kind) this.keepOnlyKind(kind);
  }

  onExecutionFilterChange(event: Event): void {
    this.executionFilter.set((event.target as HTMLSelectElement).value as PickerExecutionFilter | '');
    this.activeSavedViewName.set(null);
  }

  showOtherKind(): void {
    this.onlyMatchingKind.set(false);
  }

  /** Native Buttons: Auch Enter kommt als Klick hier an. */
  selectStep(step: 1 | 2): void {
    if (this.paketStore.umgebungAnlage()) return; // Der Wechsel zerstört die Karte und bräche die Anfrage ab.
    if (step === 2 && !this.hasTestCases()) return;
    this.chosenStep.set(step);
  }

  /** Der Fuß-Button verschwindet beim Wechsel. Auch ein gesperrter Reiter bekommt den Fokus, für seinen Hinweis. */
  goToStep(step: 1 | 2): void {
    this.selectStep(step);
    this.hostElement.nativeElement.querySelector<HTMLElement>(`#tp-step-tab-${step}`)?.focus();
  }

  onStepKeydown(event: KeyboardEvent, step: 1 | 2): void {
    const target = this.stepKeyTarget(event.key, step);
    if (target === null) return;
    event.preventDefault();
    this.goToStep(target);
  }

  private stepKeyTarget(key: string, step: 1 | 2): 1 | 2 | null {
    switch (key) {
      case 'ArrowRight':
      case 'ArrowLeft':
        return step === 1 ? 2 : 1;
      case 'Home':
        return 1;
      case 'End':
        return 2;
      default:
        return null;
    }
  }

  isSelected(tc: TestCase): boolean {
    return this.selectedIds().has(tc.id);
  }

  onSearchInput(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
    this.activeSavedViewName.set(null);
  }

  onTestTypeChange(event: Event): void {
    this.testTypeFilter.set((event.target as HTMLSelectElement).value);
    this.activeSavedViewName.set(null);
  }

  onPriorityChange(event: Event): void {
    this.priorityFilter.set((event.target as HTMLSelectElement).value as TestCasePriority | '');
    this.activeSavedViewName.set(null);
  }

  private loadSavedViews(): void {
    this.savedViewService
      .list(this.projectId, 'TEST_PLAN_PICKER')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (views) => this.savedViews.set(views),
        error: () => {
          this.savedViews.set([]);
          this.savedViewsError.set(this.translate.instant('testPlans.savedViews.loadError'));
        },
      });
  }

  private summarizeSavedView(view: SavedView): string {
    const filters = parseTestPlanPickerSavedViewFilters(view.filtersJson);
    const parts = [
      filters.testType ? tcValueLabel(this.translate, 'testType', filters.testType) : null,
      filters.priority ? this.translate.instant('testCases.priority.' + filters.priority) : null,
      filters.execution ? this.translate.instant(EXECUTION_FILTER_LABELS[filters.execution]) : null,
      this.summarizeTags(filters.tags),
    ].filter((s): s is string => !!s);
    return parts.length ? parts.join(' · ') : this.translate.instant('testPlans.savedViews.summaryAll');
  }

  private summarizeTags(tags: string[]): string | null {
    if (tags.length === 0) return null;
    return tags.length === 1
      ? this.translate.instant('tagFilter.einTag', { tag: tags[0] })
      : this.translate.instant('tagFilter.mehrereTags', { count: tags.length });
  }

  onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((v) => v.name === name);
    if (!view) return;
    const filters = parseTestPlanPickerSavedViewFilters(view.filtersJson);
    this.search.set(filters.query ?? '');
    this.testTypeFilter.set(this.availableTestTypes().find((t) => t === filters.testType) ?? '');
    this.priorityFilter.set(this.availablePriorities().find((p) => p === filters.priority) ?? '');
    const knownTags = new Set(this.testCases().flatMap((tc) => tc.tags ?? []));
    this.tagFilter.set(filters.tags.filter((tag) => knownTags.has(tag)));
    this.executionFilter.set(filters.execution ?? '');
    this.activeSavedViewName.set(view.name);
  }

  onSavedViewSaved(name: string): void {
    const filtersJson = buildTestPlanPickerSavedViewFiltersJson({
      query: this.search() || null,
      testType: this.testTypeFilter() || null,
      priority: this.priorityFilter() || null,
      tags: this.tagFilter(),
      execution: this.executionFilter() || null,
    });
    this.savedViewService
      .upsert(this.projectId, { name, context: 'TEST_PLAN_PICKER', filtersJson })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.savedViews.update((list) =>
            [...list.filter((v) => v.name !== saved.name), saved].sort((a, b) => a.name.localeCompare(b.name)),
          );
          this.activeSavedViewName.set(saved.name);
          this.savedViewsError.set(null);
        },
        error: () => this.savedViewsError.set(this.translate.instant('testPlans.savedViews.saveError')),
      });
  }

  onSavedViewDeleted(name: string): void {
    this.savedViews.update((list) => list.filter((v) => v.name !== name));
    if (this.activeSavedViewName() === name) this.activeSavedViewName.set(null);
  }

  onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('testPlans.savedViews.deleteError'));
  }

  enterAddMode(): void {
    this.addMode.set(true);
  }

  exitAddMode(): void {
    this.addMode.set(false);
  }

  /** Tauscht nur die beiden Plätze, damit nicht geladene Testfälle dazwischen ihren Platz behalten. */
  moveCase(tc: TestCase, delta: -1 | 1, event: Event): void {
    event.stopPropagation();
    const rows = this.selectedInPlanOrder();
    const neighbour = rows[rows.indexOf(tc) + delta];
    if (!neighbour || this.saving()) return;
    const ids = [...this.selectedIds()];
    const from = ids.indexOf(tc.id);
    const to = ids.indexOf(neighbour.id);
    [ids[from], ids[to]] = [ids[to], ids[from]];
    this.selectedIds.set(new Set(ids));
    this.dirty.set(true);
    afterNextRender(() => this.focusMoveArrow(tc.id, delta), { injector: this.injector });
  }

  /** Das Verschieben im DOM verliert den Fokus; am Rand ist der gedrückte Pfeil gesperrt. */
  private focusMoveArrow(caseId: string, delta: -1 | 1): void {
    const arrows = this.hostElement.nativeElement.querySelectorAll<HTMLButtonElement>(
      `[data-case-id="${caseId}"] .tpf-picker-move button`,
    );
    const [pressed, other] = delta < 0 ? [arrows[0], arrows[1]] : [arrows[1], arrows[0]];
    (pressed?.disabled ? other : pressed)?.focus();
  }

  toggleStepsExpanded(tc: TestCase, event: Event): void {
    event.stopPropagation();
    const open = this.isStepExpanded(tc);
    this.expandedStepsCaseId.set(open ? null : tc.id);
    if (!open) this.ensureStepsLoaded(tc);
  }

  private ensureStepsLoaded(tc: TestCase): void {
    if (this.stepsByCase().has(tc.id)) return;
    if (tc.steps && tc.steps.length) {
      this.applyLoadedSteps(tc.id, tc.steps);
      return;
    }
    this.loadingSteps.update((set) => {
      const next = new Set(set);
      next.add(tc.id);
      return next;
    });
    this.testCaseService
      .getById(this.projectId, tc.id)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((full) => {
        this.loadingSteps.update((set) => {
          const next = new Set(set);
          next.delete(tc.id);
          return next;
        });
        if (full && full.steps) this.applyLoadedSteps(tc.id, full.steps);
      });
  }

  private applyLoadedSteps(caseId: string, steps: TestStep[]): void {
    const ordered = [...steps].sort((a, b) => a.orderIndex - b.orderIndex);
    this.stepsByCase.update((map) => {
      const next = new Map(map);
      next.set(caseId, ordered);
      return next;
    });
  }

  isStepExpanded(tc: TestCase): boolean {
    return this.expandedStepsCaseId() === tc.id;
  }

  stepsFor(tc: TestCase): TestStep[] {
    return this.stepsByCase().get(tc.id) ?? [];
  }

  isStepLoading(tc: TestCase): boolean {
    return this.loadingSteps().has(tc.id);
  }

  /** Die Listenzahl hat Vorrang vor den geladenen Einträgen, sonst zählte ein Modul nach dem Aufklappen nur einmal. */
  stepTotal(tc: TestCase): number {
    return tc.stepCount ?? this.stepsByCase().get(tc.id)?.length ?? tc.steps?.length ?? 0;
  }

  onCancel(): void {
    this.router.navigate(['/projects', this.projectId, 'test-plans']);
  }

  /** Zuerst der Plan (nur wenn geändert), danach die Pakete (nur geladen und geändert, P30). */
  onSave(): void {
    if (this.saveDisabled()) return;
    if (this.isEdit && this.selectionDirty()) {
      this.openSelectionDialog();
      return;
    }
    this.persist();
  }

  onSelectionDialogConfirmed(): void {
    this.selectionDialog.set(null);
    this.persist(this.changeReason().trim() || undefined);
  }

  onSelectionDialogCancelled(): void {
    this.selectionDialog.set(null);
    this.changeReason.set('');
  }

  private openSelectionDialog(): void {
    const initial = this.initialSelectedIds();
    const current = this.selectedIds();
    const added = [...current].filter((id) => !initial.has(id));
    const removed = [...initial].filter((id) => !current.has(id));
    const keys = new Map(this.testCases().map((tc) => [tc.id, tc.key ?? tc.id]));
    const keyOf = (id: string): string => keys.get(id) ?? id;
    this.selectionRuns.set(null);
    this.selectionRunsLoading.set(true);
    this.selectionDialog.set({
      added: added.map(keyOf),
      removed: removed.map(keyOf),
      // Wie das Backend: Wer im selben Speichern einen anderen Status wählt, öffnet den Plan nicht automatisch.
      reopensPlan:
        added.length > 0 &&
        this.plan()?.status === 'ABGESCHLOSSEN' &&
        this.form.controls.status.value === 'ABGESCHLOSSEN',
    });
    this.planService
      .getRuns(this.projectId, this.planId!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (runs) => {
          this.selectionRuns.set(runs);
          this.selectionRunsLoading.set(false);
        },
        error: () => this.selectionRunsLoading.set(false),
      });
  }

  private persist(changeReason?: string): void {
    const raw = this.form.getRawValue();
    const testCaseIds = Array.from(this.selectedIds());
    const unchangedPlan = this.isEdit && !this.dirty() && !this.selectionDirty() ? this.plan() : null;
    const sendPackages = this.paketStore.ladeStatus() === 'bereit' && this.paketStore.geaendert();
    this.setSaving(true);
    this.saveError.set(false);
    const selectionSent = !this.isEdit || this.selectionChanged(testCaseIds);
    const plan$ = unchangedPlan
      ? of(unchangedPlan)
      : this.savePlanRequest(raw, testCaseIds, selectionSent, changeReason);

    plan$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.changeReason.set('');
        // Ungesendet gilt die Antwort: Die eigene Liste kennt fremd aufgenommene Testfälle nicht, die Pakete behalten sie.
        const planCaseIds = selectionSent ? testCaseIds : saved.testCaseIds;
        if (!unchangedPlan) this.applySavedPlan(saved, planCaseIds, raw.tags, selectionSent);
        if (!sendPackages) {
          this.finishSave(saved);
          return;
        }
        this.paketStore
          .speichern(saved.id, planCaseIds)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.finishSave(saved),
            error: (err: unknown) => this.onPackagesSaveFailed(saved, err),
          });
      },
      error: (err: unknown) => {
        this.setSaving(false);
        const tagsRejected = isTagsInvalidResponse(err);
        // Sind die Plan-Tags gültig, kann nur die Regel gemeint sein; der Fehler gehört dann nicht ans Tag-Feld.
        const ruleRejected =
          tagsRejected && this.ruleChanged() && this.ruleTags().length > 0 && !tagsValidator(this.form.controls.tags);
        if (tagsRejected && !ruleRejected) {
          this.form.controls.tags.setErrors({ [TAGS_ERROR]: true });
          return;
        }
        this.saveErrorKey.set(
          ruleRejected ? 'testPlans.form.rule.tagsInvalid' : writeErrorKey(err, 'testPlans.form.saveError'),
        );
        this.saveError.set(true);
      },
    });
  }

  private applySavedPlan(saved: TestPlan, testCaseIds: string[], sentTags: string[], selectionSent: boolean): void {
    this.dirty.set(false);
    this.paketStore.behalteNurTestfaelle(testCaseIds);
    if (!this.isEdit) {
      this.form.markAsPristine();
      return;
    }
    this.plan.set(saved);
    this.form.patchValue(
      {
        status: saved.status,
        type: saved.type ?? this.form.controls.type.value,
        tags: saved.tags ?? sentTags,
        notes: saved.notes ?? '',
      },
      { emitEvent: false },
    );
    this.form.markAsPristine();
    // Sonst gälte eine fremde Regel aus der Antwort beim nächsten Speichern als hier entfernt.
    this.ruleTags.set(saved.selectionRuleTags ?? []);
    // Das Backend nimmt bei aktiver Regel Treffer auf, auch einen eben abgewählten eines neuen Regel-Tags.
    if (!selectionSent || this.ruleTags().length > 0) {
      this.selectedIds.set(new Set(saved.testCaseIds));
      this.initialSelectedIds.set(new Set(saved.testCaseIds));
    } else {
      this.initialSelectedIds.set(new Set(testCaseIds));
    }
    this.dropStepSelectionsOfRemovedCases();
  }

  /** Ein später wieder gewählter Testfall gilt als neu und läuft ohne Beschränkung. */
  private dropStepSelectionsOfRemovedCases(): void {
    const kept = this.selectedIds();
    this.stepSelections.update((map) => new Map([...map].filter(([caseId]) => kept.has(caseId))));
  }

  private finishSave(saved: TestPlan): void {
    this.setSaving(false);
    if (this.isEdit) {
      this.saveToast.show(this.translate.instant('testPlans.form.saved'));
      this.historieNeuLaden();
      return;
    }
    this.saveToast.show(this.translate.instant('testPlans.form.created'));
    this.router.navigate(['/projects', this.projectId, 'test-plans', saved.id, 'edit'], { replaceUrl: true });
  }

  // Auch die Kette: Beim Bestandsplan leitet das BE die Anzeige-Stufe aus dem Verantwortlichen ab.
  private historieNeuLaden(): void {
    this.historie()?.ladeKette();
    this.historie()?.ladeVerlauf();
  }

  /** Der Plan ist dann schon gespeichert; der Paket-Entwurf bleibt erhalten (P27, P32). */
  private onPackagesSaveFailed(saved: TestPlan, err: unknown): void {
    this.setSaving(false);
    if (!this.isEdit) {
      this.snackBar.open(this.translate.instant('testPlans.packages.createdWithoutPackages'), undefined, {
        duration: 5000,
      });
      this.leaveWithoutPrompt = true;
      this.router.navigate(['/projects', this.projectId, 'test-plans', saved.id, 'edit'], {
        replaceUrl: true,
        state: { [PENDING_PACKAGES]: this.paketStore.entwurf() },
      });
      return;
    }
    this.historieNeuLaden();
    if (isProjectArchived(err)) return;
    const message = isStaleVersion(err)
      ? this.translate.instant('testPlans.packages.saveStale')
      : (serverMessage(err) ?? this.translate.instant('testPlans.packages.saveError'));
    this.snackBar.open(message, undefined, { duration: 5000 });
  }

  private savePlanRequest(
    raw: ReturnType<FormGroup<TestPlanFormShape>['getRawValue']>,
    testCaseIds: string[],
    selectionSent: boolean,
    changeReason?: string,
  ): Observable<TestPlan> {
    const stepSelections = this.buildStepSelectionsPayload(testCaseIds);
    // Das Select zeigt den wirksamen Status; unverändert mitgeschickt, würde ein abgeleitetes Aktiv als Handwert gespeichert.
    const statusChanged = this.form.controls.status.dirty;
    const executionKind = this.planKind() ?? undefined;
    // Unverändert mitgeschickt, überschriebe das Speichern eine inzwischen geänderte fremde Notiz.
    const notesChanged = this.form.controls.notes.dirty;
    // Ebenso die Regel: Ein mitgeschicktes [] entfernte eine inzwischen fremd gesetzte Regel still.
    // Und die Auswahl: Die alte Liste nähme einen inzwischen per Regel aufgenommenen Testfall aus Plan und Paketen.
    return this.isEdit && this.planId
      ? this.planService.update(this.projectId, this.planId, {
          title: raw.title.trim(),
          releaseId: raw.releaseId,
          ...(statusChanged ? { status: raw.status } : {}),
          ...(this.form.controls.type.dirty ? { type: raw.type } : {}),
          assigneeUserId: raw.assigneeUserId || null,
          clearAssignee: !raw.assigneeUserId,
          ...(selectionSent ? { testCaseIds } : {}),
          stepSelections,
          tags: raw.tags,
          executionKind,
          ...(this.ruleChanged() ? { selectionRuleTags: this.ruleTags() } : {}),
          ...(notesChanged ? { notes: raw.notes } : {}),
          ...(changeReason ? { changeReason } : {}),
        })
      : this.planService.create(this.projectId, {
          title: raw.title.trim(),
          releaseId: raw.releaseId,
          status: raw.status,
          type: raw.type,
          assigneeUserId: raw.assigneeUserId || null,
          testCaseIds,
          stepSelections,
          tags: raw.tags,
          executionKind,
          selectionRuleTags: this.ruleTags(),
          ...(raw.notes ? { notes: raw.notes } : {}),
        });
  }

  private ruleChanged(): boolean {
    return !sameRule(this.ruleTags(), this.plan()?.selectionRuleTags ?? []);
  }

  // Anders als selectionDirty zählt hier die Reihenfolge, sonst gingen die Pfeile verloren.
  private selectionChanged(testCaseIds: string[]): boolean {
    const initial = [...this.initialSelectedIds()];
    return testCaseIds.length !== initial.length || testCaseIds.some((id, index) => id !== initial[index]);
  }

  private setSaving(saving: boolean): void {
    this.saving.set(saving);
    if (saving) {
      this.form.disable({ emitEvent: false });
      return;
    }
    this.form.enable({ emitEvent: false });
    // applySavedPlan belegt das gesperrte Formular ohne Events vor; Status- und Tags-Signale erfahren es erst hier.
    this.form.updateValueAndValidity();
  }

  private buildStepSelectionsPayload(caseIds: string[]): Record<string, number[]> {
    const out: Record<string, number[]> = {};
    const selections = this.stepSelections();
    for (const id of caseIds) {
      const sel = selections.get(id);
      if (!sel) continue;
      out[id] = Array.from(sel).sort((a, b) => a - b);
    }
    return out;
  }

  onEchtlauf(): void {
    if (!this.planId || this.echtlaufBlock()) return;
    this.router.navigate(['/projects', this.projectId, 'test-runs', 'new'], {
      queryParams: realRunQueryParams(this.planId, this.executionKind() === 'AUTOMATED'),
    });
  }

  onTagInput(input: HTMLInputElement): void {
    if (input.value.endsWith(',')) this.addTag(input);
  }

  onTagKeydown(event: KeyboardEvent, input: HTMLInputElement): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag(input);
    } else if (event.key === 'Backspace' && !input.value) {
      const tags = this.form.controls.tags.value;
      if (tags.length) this.setTags(tags.slice(0, -1));
    }
  }

  onTagBlur(input: HTMLInputElement): void {
    if (input.value) this.addTag(input);
  }

  removeTag(tag: string): void {
    this.setTags(this.form.controls.tags.value.filter((t) => t !== tag));
  }

  private addTag(input: HTMLInputElement): void {
    const tag = input.value.replace(/,+$/, '').trim().toLowerCase();
    input.value = '';
    const tags = this.form.controls.tags.value;
    if (tag && !tags.includes(tag)) this.setTags([...tags, tag]);
  }

  private setTags(tags: string[]): void {
    this.form.controls.tags.setValue(tags);
    this.form.controls.tags.markAsDirty();
    this.dirty.set(true);
  }

  /** Gezählt wie in den Testergebnissen (NEX-2028); Läufe aus Testfällen erzeugen keine Plan-Ausführung. */
  private readonly hasCountedResults = computed(() => {
    const progress = this.plan()?.progress;
    return !!progress && progress.passed + progress.failed + progress.blocked > 0;
  });
  readonly canOpenResults = computed(() => this.runCompleted() || this.hasCountedResults());

  private startPolling(planId: string): void {
    this.stopPolling();
    this.pollingSub = timer(2000, 2000)
      .pipe(
        switchMap(() => this.planService.getExecution(this.projectId, planId).pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((exec) => {
        if (!exec) return;
        this.applyExecution(exec);
        if (exec.status === 'DONE' || exec.status === 'FAILED') {
          this.stopPolling();
        }
      });
  }

  private applyExecution(exec: TestPlanExecution): void {
    this.execution.set(exec);
    this.createdRunSub?.unsubscribe();
    this.createdRunSub = null;
    this.createdRun.set(null);
    if (exec.status !== 'DONE' || !exec.testRunId) return;
    this.createdRunSub = this.testRunService
      .get(this.projectId, exec.testRunId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((run) => this.createdRun.set(run));
  }

  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = null;
  }

  hasUnsavedChanges(): boolean {
    return (this.dirty() || this.paketStore.geaendert()) && !this.saving() && !this.leaveWithoutPrompt;
  }

  confirmDeactivate(): Observable<boolean> {
    if (!this.hasUnsavedChanges()) return of(true);
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }
}
